import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Form, Spinner } from "react-bootstrap";
import {
  FaCalendarAlt,
  FaCheck,
  FaCloudUploadAlt,
  FaExternalLinkAlt,
  FaHistory,
  FaImage,
  FaInfoCircle,
  FaLayerGroup,
  FaLightbulb,
  FaListOl,
  FaPen,
  FaRegImages,
  FaSyncAlt,
  FaTrash,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { getUserFromLocalStorage } from "utilities/user.js";
import { useCountry } from "../../context/CountryContext.js";
import axios from "axios";
import moment from "moment";
import "./FeaturedImageUpload.css";
import ConfirmDialog from "../../components/ConfirmDialog";
import AppSelect from "../../components/ui/AppSelect";

const API_URL = process.env.REACT_APP_SERVER_URL;
// Use existing preset temporarily, or create "brands_frontpage" in Cloudinary
const CLOUDINARY_UPLOAD_PRESET = "brands_frontpage"; // Change to "lusuku-clou" if preset doesn't exist yet
const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const EMPTY_ARTWORK = Object.freeze({ web: null, mobile: null });
const ARTWORK_SPECS = Object.freeze({
  web: {
    label: "Web shop carousel",
    width: 1500,
    height: 400,
    ratio: 15 / 4,
    suffix: "web",
  },
  mobile: {
    label: "Mobile carousel",
    width: 1200,
    height: 675,
    ratio: 16 / 9,
    suffix: "mobile",
  },
});
const createDraftKey = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

const FeaturedImageUpload = () => {
  const location = useLocation();
  const brandIdFromQuery = new URLSearchParams(location.search).get("brandId") || "";
  const [brands, setBrands] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState(brandIdFromQuery);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedDate, setSelectedDate] = useState(
    moment().format("YYYY-MM-DD")
  );
  const [caption, setCaption] = useState("");
  const [featuredImageHistory, setFeaturedImageHistory] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  // Featured image awaiting delete confirmation. null = closed.
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const [draftArtwork, setDraftArtwork] = useState(EMPTY_ARTWORK);
  const [draftKey, setDraftKey] = useState(createDraftKey);
  const [editingEditionId, setEditingEditionId] = useState(null);
  const [error, setError] = useState(null);
  const [cloudinaryLoaded, setCloudinaryLoaded] = useState(false);

  const user = getUserFromLocalStorage();
  const { isSuperAdmin: hasSuperAdminCapability } = useCountry();
  const adminRole = String(
    user?.result?.user_role || user?.result?.role || ""
  ).toUpperCase();
  const isSuperAdmin =
    hasSuperAdminCapability ||
    adminRole === "SUPERADMIN" ||
    adminRole === "SUPERUSER";

  useEffect(() => {
    fetchBrands();
    loadCloudinaryScript();
  }, []);

  useEffect(() => {
    const requestedBrandId =
      new URLSearchParams(location.search).get("brandId") || "";
    if (requestedBrandId) setSelectedBrandId(requestedBrandId);
  }, [location.search]);

  // Load Cloudinary script
  const loadCloudinaryScript = () => {
    if (window.cloudinary) {
      setCloudinaryLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://widget.cloudinary.com/v2.0/global/all.js";
    script.async = true;
    script.onload = () => {
      setCloudinaryLoaded(true);
    };
    script.onerror = () => {
      toast.error("Failed to load Cloudinary. Please refresh the page.");
    };
    document.body.appendChild(script);
  };

  useEffect(() => {
    if (selectedBrandId) {
      fetchFeaturedImageHistory(selectedBrandId);
      const pub = brands.find((p) => p._id === selectedBrandId);
      setSelectedBrand(pub);
      setDraftArtwork(EMPTY_ARTWORK);
      setEditingEditionId(null);
      setDraftKey(createDraftKey());
    } else {
      setSelectedBrand(null);
      setFeaturedImageHistory([]);
      setDraftArtwork(EMPTY_ARTWORK);
      setEditingEditionId(null);
      setDraftKey(createDraftKey());
    }
  }, [selectedBrandId, brands]);

  useEffect(() => {
    setDraftArtwork(EMPTY_ARTWORK);
  }, [selectedDate]);

  const fetchBrands = async () => {
    setBrandsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/brands`);
      setBrands(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || "Unable to load brands");
      toast.error("Failed to fetch brands");
    } finally {
      setBrandsLoading(false);
    }
  };

  const fetchFeaturedImageHistory = async (brandId) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/brands/${brandId}/featured-image/history`
      );
      setFeaturedImageHistory(response.data.data || []);
    } catch (err) {
      console.error("Failed to fetch history:", err);
      setFeaturedImageHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCloudinaryUpload = (target) => {
    const artworkSpec = ARTWORK_SPECS[target];
    if (!artworkSpec) return;

    if (!selectedBrandId) {
      toast.error("Please select a brand first");
      return;
    }

    if (!selectedDate) {
      toast.error("Please select a date");
      return;
    }

    // Check if Cloudinary script is loaded
    if (!cloudinaryLoaded) {
      toast.warn(
        "Cloudinary is still loading. Please wait a moment and try again."
      );
      return;
    }

    // Validate Cloudinary configuration
    if (!CLOUDINARY_CLOUD_NAME) {
      toast.error(
        "Cloudinary cloud name is not configured. Please check your environment variables."
      );
      return;
    }

    if (!CLOUDINARY_UPLOAD_PRESET) {
      toast.error(
        "Cloudinary upload preset is not configured. Please check your environment variables."
      );
      return;
    }

    if (!window.cloudinary || !window.cloudinary.createUploadWidget) {
      toast.error(
        "Cloudinary upload widget is not available. Please refresh the page."
      );
      return;
    }

    const widget = window.cloudinary.createUploadWidget(
      {
        cloudName: CLOUDINARY_CLOUD_NAME,
        uploadPreset: CLOUDINARY_UPLOAD_PRESET,
        folder: `brands/${selectedBrand?.name}`,
        // Date alone used to collide when a brand added a second slide that
        // day. One draft key is shared by its web/mobile pair, then renewed.
        publicId: `frontpage_${moment(selectedDate).format("YYYY-MM-DD")}_${draftKey}_${artworkSpec.suffix}`,
        maxFileSize: 5000000, // 5MB
        clientAllowedFormats: ["jpg", "jpeg", "png", "webp"],
        multiple: false,
        // Cloudinary only exposes its interactive crop step for single-file
        // uploads. The chosen region is sent as custom coordinates and used by
        // the incoming transformation below instead of being centre-cropped.
        cropping: true,
        croppingCoordinatesMode: "custom",
        croppingAspectRatio: artworkSpec.ratio,
        croppingDefaultSelectionRatio: 0.9,
        croppingShowDimensions: true,
        croppingShowBackButton: true,
        showSkipCropButton: false,
        singleUploadAutoClose: false,
        showCompletedButton: true,
        showUploadMoreButton: false,
        transformation: [
          {
            width: artworkSpec.width,
            height: artworkSpec.height,
            crop: "fill",
            gravity: "custom",
            quality: "auto:best",
            fetch_format: "auto",
          },
        ],
        sources: ["local", "url", "camera"],
        showPoweredBy: false,
        styles: {
          palette: {
            window: "#FFFFFF",
            windowBorder: "#1976D2",
            tabIcon: "#1976D2",
            menuIcons: "#1976D2",
            textDark: "#000000",
            textLight: "#FFFFFF",
            link: "#1976D2",
            action: "#1976D2",
            inactiveTabIcon: "#999999",
            error: "#F44336",
            inProgress: "#1976D2",
            complete: "#4CAF50",
            sourceBg: "#F5F5F5",
          },
          fonts: {
            default: null,
            "'Poppins', sans-serif": {
              url: "https://fonts.googleapis.com/css2?family=Poppins:wght@400;600&display=swap",
              active: true,
            },
          },
        },
      },
      (error, result) => {
        if (error) {
          console.error("Cloudinary upload error:", error);
          toast.error("Upload failed. Please try again.");
          setUploading(false);
          setUploadTarget(null);
          return;
        }

        if (result.event === "upload-started") {
          setUploading(true);
          setUploadTarget(target);
          setUploadProgress(0);
        }

        if (result.event === "progress") {
          setUploadProgress(Math.round(result.info.progress));
        }

        if (result.event === "success") {
          setDraftArtwork((current) => ({
            ...current,
            [target]: result.info,
          }));
          setUploading(false);
          setUploadTarget(null);
          setUploadProgress(0);
          toast.success(`${artworkSpec.label} crop is ready.`);
        }

        if (result.event === "show-completed") {
          widget.close({ quiet: true });
        }
      }
    );

    widget.open();
  };

  const handlePublishEdition = async () => {
    if (!draftArtwork.web && !draftArtwork.mobile) {
      toast.error("Add web artwork, mobile artwork, or both before publishing.");
      return;
    }

    try {
      setPublishing(true);

      // Get user ID for uploadedBy field
      const userId = user?.result?._id;
      if (!userId) {
        toast.error("User information not found. Please log in again.");
        setPublishing(false);
        return;
      }

      const payload = {
        publicationDate: selectedDate,
        uploadedBy: userId,
        ...(editingEditionId ? { featuredImageId: editingEditionId } : {}),
        ...(caption.trim() ? { caption: caption.trim() } : {}),
      };

      if (draftArtwork.mobile) {
        Object.assign(payload, {
          // Legacy aliases keep older mobile clients working during rollout.
          featuredImageUrl: draftArtwork.mobile.secure_url,
          cloudinaryPublicId: draftArtwork.mobile.public_id,
          mobileImageUrl: draftArtwork.mobile.secure_url,
          mobileCloudinaryPublicId: draftArtwork.mobile.public_id,
        });
      }
      if (draftArtwork.web) {
        Object.assign(payload, {
          webImageUrl: draftArtwork.web.secure_url,
          webCloudinaryPublicId: draftArtwork.web.public_id,
        });
      }

      const response = await axios.post(
        `${API_URL}/api/brands/${selectedBrandId}/featured-image`,
        payload
      );

      const publishedChannels = response.data?.publishedChannels || [];
      const channelLabel =
        publishedChannels.length === 2
          ? "Web and mobile artwork"
          : publishedChannels[0] === "web"
            ? "Web artwork"
            : "Mobile artwork";
      toast.success(
        `${channelLabel} ${editingEditionId ? "updated" : "added to the carousel"} successfully!`
      );
      const savedEdition = response.data?.data;
      if (savedEdition?._id) {
        setFeaturedImageHistory((current) =>
          [
            savedEdition,
            ...current.filter((edition) => edition._id !== savedEdition._id),
          ].sort(
            (a, b) => {
              const dateDifference =
                new Date(b.publicationDate || b.date).getTime() -
                new Date(a.publicationDate || a.date).getTime();
              if (dateDifference) return dateDifference;
              return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            }
          )
        );
      }
      setDraftArtwork(EMPTY_ARTWORK);
      setCaption("");
      setEditingEditionId(null);
      setDraftKey(createDraftKey());
      await fetchFeaturedImageHistory(selectedBrandId);
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to save featured image"
      );
    } finally {
      setPublishing(false);
    }
  };

  const handleDeleteFeaturedImage = (featuredImageId) =>
    setDeleteConfirm(featuredImageId);

  const handleEditFeaturedImage = (featuredImage) => {
    setEditingEditionId(featuredImage._id);
    setSelectedDate(
      moment(featuredImage.publicationDate || featuredImage.date).format("YYYY-MM-DD")
    );
    setCaption(featuredImage.caption || "");
    setDraftArtwork(EMPTY_ARTWORK);
    setDraftKey(createDraftKey());
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleStartNewSlide = () => {
    setEditingEditionId(null);
    setCaption("");
    setDraftArtwork(EMPTY_ARTWORK);
    setDraftKey(createDraftKey());
  };

  const performDeleteFeaturedImage = async (featuredImageId) => {
    setDeletingId(featuredImageId);
    try {
      await axios.delete(
        `${API_URL}/api/brands/featured-image/${featuredImageId}`
      );
      toast.success("Featured image deleted successfully!");
      if (editingEditionId === featuredImageId) handleStartNewSlide();
      fetchFeaturedImageHistory(selectedBrandId);
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to delete featured image"
      );
    } finally {
      setDeletingId(null);
      setDeleteConfirm(null);
    }
  };

  const assetUploadDisabled =
    !isSuperAdmin ||
    !selectedBrandId ||
    !selectedDate ||
    uploading ||
    publishing ||
    !cloudinaryLoaded;
  const publishDisabled =
    assetUploadDisabled ||
    (!draftArtwork.web && !draftArtwork.mobile);

  const editingEdition = editingEditionId
    ? featuredImageHistory.find((edition) => edition._id === editingEditionId)
    : null;
  const existingWebImageUrl = editingEdition?.webImageUrl || "";
  const existingMobileImageUrl =
    editingEdition?.mobileImageUrl ||
    editingEdition?.featuredImageUrl ||
    "";
  const webPreviewUrl = draftArtwork.web?.secure_url || existingWebImageUrl;
  const mobilePreviewUrl =
    draftArtwork.mobile?.secure_url || existingMobileImageUrl;
  const webStatus = draftArtwork.web
    ? "Ready"
    : existingWebImageUrl
      ? "Published"
      : "Optional";
  const mobileStatus = draftArtwork.mobile
    ? "Ready"
    : existingMobileImageUrl
      ? "Published"
      : "Optional";
  const channelPublishLabel =
    draftArtwork.web && draftArtwork.mobile
      ? "web + mobile artwork"
      : draftArtwork.web
        ? "web artwork"
        : draftArtwork.mobile
          ? "mobile artwork"
          : "artwork";
  const publishLabel = draftArtwork.web || draftArtwork.mobile
    ? `${editingEditionId ? "Update" : "Add slide with"} ${channelPublishLabel}`
    : "Choose web or mobile artwork";

  return (
    <>
      <ConfirmDialog
        show={!!deleteConfirm}
        tone="danger"
        title="Delete featured image?"
        body="This permanently removes the featured image from this brand and from Cloudinary."
        note="This cannot be undone. You can upload a replacement afterwards."
        confirmLabel="Delete image"
        busyLabel="Deleting…"
        busy={!!deleteConfirm && deletingId === deleteConfirm}
        onConfirm={() => performDeleteFeaturedImage(deleteConfirm)}
        onCancel={() => setDeleteConfirm(null)}
      />
      <main className="featured-studio">
      <header className="featured-studio__header">
        <div className="featured-studio__title-group">
          <span className="featured-studio__eyebrow">Brand merchandising</span>
          <h1>
            <span className="featured-studio__title-icon">
              <FaLayerGroup />
            </span>
            Carousel Artwork
          </h1>
          <p>
            Prepare and publish artwork for the Featured Brands cards and the
            brand shop carousel.
          </p>
        </div>
        <div className="featured-studio__status">
          <span className={`featured-status ${cloudinaryLoaded ? "is-ready" : "is-loading"}`}>
            <span className="featured-status__dot" />
            {cloudinaryLoaded ? "Media service ready" : "Connecting media service"}
          </span>
          <span className="featured-status">
            <FaCalendarAlt />
            {moment(selectedDate).format("DD MMM YYYY")}
          </span>
        </div>
      </header>

      {!isSuperAdmin && (
        <div className="featured-notice featured-notice--warning" role="alert">
          <FaInfoCircle />
          <div>
            <strong>Superadmin access required</strong>
            <span>Your account can review editions but cannot publish or remove artwork.</span>
          </div>
        </div>
      )}

      {error && (
        <div className="featured-notice featured-notice--error" role="alert">
          <FaInfoCircle />
          <div>
            <strong>Brand data unavailable</strong>
            <span>{error}</span>
          </div>
          <button type="button" onClick={fetchBrands}>Retry</button>
        </div>
      )}

      <div className="featured-studio__workspace">
        <section className="featured-panel featured-composer">
          <div className="featured-panel__header">
            <div>
              <span className="featured-panel__kicker">
                {editingEditionId ? "Editing carousel slide" : "New carousel slide"}
              </span>
              <h2>{editingEditionId ? "Update artwork" : "Compose artwork"}</h2>
            </div>
            {editingEditionId ? (
              <button
                type="button"
                className="featured-new-slide-button"
                onClick={handleStartNewSlide}
                disabled={uploading || publishing}
              >
                <FaRegImages /> New slide
              </button>
            ) : (
              <span className="featured-panel__step">01</span>
            )}
          </div>

          <Form className="featured-form">
            <div className="featured-form__row">
              <Form.Group>
                <Form.Label>Brand</Form.Label>
                <div className="featured-control-wrap">
                  <FaLayerGroup />
                  <AppSelect block
                    value={selectedBrandId}
                    onChange={(e) => setSelectedBrandId(e.target.value)}
                    disabled={!isSuperAdmin || brandsLoading}
                  >
                    <option value="">
                      {brandsLoading ? "Loading brands..." : "Choose a brand"}
                    </option>
                    {brands.map((brand) => (
                      <option key={brand._id} value={brand._id}>
                        {brand.name}
                      </option>
                    ))}
                  </AppSelect>
                </div>
              </Form.Group>

              <Form.Group>
                <Form.Label>Edition date</Form.Label>
                <div className="featured-control-wrap">
                  <FaCalendarAlt />
                  <Form.Control
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    max={moment().format("YYYY-MM-DD")}
                    disabled={!isSuperAdmin || !selectedBrandId}
                  />
                </div>
              </Form.Group>
            </div>

            <Form.Group>
              <div className="featured-label-row">
                <Form.Label>Caption</Form.Label>
                <span>{caption.length}/160</span>
              </div>
              <Form.Control
                as="textarea"
                rows={2}
                maxLength={160}
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Optional carousel caption"
                disabled={!isSuperAdmin || !selectedBrandId}
              />
            </Form.Group>
          </Form>

          <div className="featured-asset-list">
            <section className="featured-asset-card">
              <div className="featured-asset-card__header">
                <div>
                  <span>Web artwork</span>
                  <strong>Shop carousel · 1500 × 400 · 15:4</strong>
                </div>
                <span className={webPreviewUrl ? "is-ready" : ""}>
                  {webStatus}
                </span>
              </div>
              <div
                className={`featured-dropzone featured-dropzone--web ${selectedBrandId ? "is-enabled" : ""} ${webPreviewUrl ? "has-preview" : ""}`}
              >
                {webPreviewUrl ? (
                  <>
                    <img src={webPreviewUrl} alt="Web carousel artwork preview" />
                    <div className="featured-dropzone__preview-label">
                      <FaCheck /> {draftArtwork.web ? "Crop ready" : "Published"}
                    </div>
                    <button
                      type="button"
                      className="featured-dropzone__replace"
                      onClick={() => handleCloudinaryUpload("web")}
                      disabled={assetUploadDisabled}
                    >
                      Replace web
                    </button>
                  </>
                ) : (
                  <div className="featured-dropzone__empty featured-dropzone__empty--wide">
                    <strong>Wide web banner</strong>
                    <span>Designed to fill the desktop carousel without blurred side panels.</span>
                    <button
                      type="button"
                      onClick={() => handleCloudinaryUpload("web")}
                      disabled={assetUploadDisabled}
                    >
                      <FaRegImages /> Choose &amp; crop 15:4
                    </button>
                  </div>
                )}
              </div>
            </section>

            <section className="featured-asset-card">
              <div className="featured-asset-card__header">
                <div>
                  <span>Mobile artwork</span>
                  <strong>App carousel · 1200 × 675 · 16:9</strong>
                </div>
                <span className={mobilePreviewUrl ? "is-ready" : ""}>
                  {mobileStatus}
                </span>
              </div>
              <div
                className={`featured-dropzone featured-dropzone--mobile ${selectedBrandId ? "is-enabled" : ""} ${mobilePreviewUrl ? "has-preview" : ""}`}
              >
                {mobilePreviewUrl ? (
                  <>
                    <img src={mobilePreviewUrl} alt="Mobile carousel artwork preview" />
                    <div className="featured-dropzone__preview-label">
                      <FaCheck /> {draftArtwork.mobile ? "Crop ready" : "Published"}
                    </div>
                    <button
                      type="button"
                      className="featured-dropzone__replace"
                      onClick={() => handleCloudinaryUpload("mobile")}
                      disabled={assetUploadDisabled}
                    >
                      Replace mobile
                    </button>
                  </>
                ) : (
                  <div className="featured-dropzone__empty">
                    <span className="featured-dropzone__icon">
                      <FaCloudUploadAlt />
                    </span>
                    <strong>
                      {selectedBrand
                        ? `Mobile artwork for ${selectedBrand.name}`
                        : "Select a brand to begin"}
                    </strong>
                    <span>Safe for the mobile app carousel and 16:9 brand cards.</span>
                    <button
                      type="button"
                      onClick={() => handleCloudinaryUpload("mobile")}
                      disabled={assetUploadDisabled}
                    >
                      <FaRegImages /> Choose &amp; crop 16:9
                    </button>
                  </div>
                )}
              </div>
            </section>
          </div>

          <div className="featured-specs" aria-label="Artwork specifications">
            <div>
              <span>Web</span>
              <strong>1500 × 400 · 15:4</strong>
            </div>
            <div>
              <span>Mobile</span>
              <strong>1200 × 675 · 16:9</strong>
            </div>
            <div>
              <span>Formats</span>
              <strong>JPG · PNG · WEBP</strong>
            </div>
            <div>
              <span>Each file</span>
              <strong>5 MB maximum</strong>
            </div>
          </div>

          {uploading && (
            <div className="featured-progress" aria-live="polite">
              <div className="featured-progress__meta">
                <span>
                  Cropping and uploading {ARTWORK_SPECS[uploadTarget]?.label || "artwork"}
                </span>
                <strong>{uploadProgress}%</strong>
              </div>
              <div className="featured-progress__track">
                <span style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          )}

          <button
            type="button"
            className="featured-publish-button"
            onClick={handlePublishEdition}
            disabled={publishDisabled}
          >
            {publishing ? (
              <>
                <Spinner animation="border" size="sm" />
                Publishing artwork
              </>
            ) : !cloudinaryLoaded ? (
              <>
                <Spinner animation="border" size="sm" />
                Preparing uploader
              </>
            ) : (
              <>
                <FaCheck />
                {publishLabel}
              </>
            )}
          </button>

          <div className="featured-destination">
            <FaInfoCircle />
            <span>
              {editingEditionId
                ? "Only the newly selected format is replaced; the other format on this slide is preserved."
                : "Every publish adds a new carousel slide, even when another slide uses the same date."}
            </span>
          </div>
        </section>

        <section className="featured-panel featured-history">
          <div className="featured-panel__header">
            <div>
              <span className="featured-panel__kicker">Edition archive</span>
              <h2>{selectedBrand ? selectedBrand.name : "Recent artwork"}</h2>
            </div>
            <span className="featured-history__count">
              {selectedBrandId ? featuredImageHistory.length : "—"}
            </span>
          </div>

          {!selectedBrandId ? (
            <div className="featured-empty">
              <span><FaHistory /></span>
              <strong>No brand selected</strong>
              <p>Choose a brand to review its five most recent editions.</p>
            </div>
          ) : loading ? (
            <div className="featured-empty">
              <Spinner animation="border" />
              <strong>Loading editions</strong>
            </div>
          ) : featuredImageHistory.length === 0 ? (
            <div className="featured-empty">
              <span><FaImage /></span>
              <strong>No editions yet</strong>
              <p>The first published artwork will appear here.</p>
            </div>
          ) : (
            <div className="featured-history__list">
              {featuredImageHistory.map((featuredImage, index) => {
                const editionDate =
                  featuredImage.publicationDate || featuredImage.date;
                const isToday = moment(editionDate).isSame(moment(), "day");
                const mobileImageUrl =
                  featuredImage.mobileImageUrl || featuredImage.featuredImageUrl;
                const webImageUrl = featuredImage.webImageUrl;
                const previewImageUrl = mobileImageUrl || webImageUrl;
                const hasMobileVariant = Boolean(mobileImageUrl);
                const hasWebVariant = Boolean(featuredImage.webImageUrl);
                const channelLabel =
                  hasMobileVariant && hasWebVariant
                    ? "Web + mobile"
                    : hasWebVariant
                      ? "Web only · add mobile"
                      : "Mobile only · add web";

                return (
                  <article className="featured-history__item" key={featuredImage._id}>
                    <button
                      type="button"
                      className="featured-history__image"
                      onClick={() => window.open(previewImageUrl, "_blank")}
                      title={`Open ${hasMobileVariant ? "mobile" : "web"} artwork`}
                    >
                      <img
                        src={previewImageUrl}
                        alt={`Artwork for ${moment(editionDate).format("DD MMM YYYY")}`}
                      />
                      <span><FaExternalLinkAlt /></span>
                    </button>
                    <div className="featured-history__body">
                      <div className="featured-history__meta">
                        <span className="featured-edition-number">
                          Edition {String(index + 1).padStart(2, "0")}
                        </span>
                        {isToday && <span className="featured-today">Today</span>}
                        <span className={hasWebVariant && hasMobileVariant ? "featured-responsive" : "featured-legacy"}>
                          {channelLabel}
                        </span>
                      </div>
                      <h3>{moment(editionDate).format("DD MMMM YYYY")}</h3>
                      {featuredImage.caption && <p>{featuredImage.caption}</p>}
                      <span className="featured-history__uploaded">
                        Published {moment(featuredImage.createdAt).format("DD MMM · HH:mm")}
                      </span>
                    </div>
                    {isSuperAdmin && (
                      <div className="featured-history__actions">
                        <button
                          type="button"
                          className="featured-edit-button"
                          onClick={() => handleEditFeaturedImage(featuredImage)}
                          disabled={uploading || publishing}
                          title="Edit this slide or add its missing format"
                          aria-label={`Edit carousel slide from ${moment(editionDate).format("DD MMMM YYYY")}`}
                        >
                          <FaPen />
                        </button>
                        <button
                          type="button"
                          className="featured-delete-button"
                          onClick={() => handleDeleteFeaturedImage(featuredImage._id)}
                          disabled={deletingId === featuredImage._id}
                          title="Delete slide"
                          aria-label={`Delete carousel slide from ${moment(editionDate).format("DD MMMM YYYY")}`}
                        >
                          {deletingId === featuredImage._id ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <FaTrash />
                          )}
                        </button>
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <section className="featured-guidance" aria-labelledby="featured-guidance-title">
        <div className="featured-guidance__header">
          <div>
            <span className="featured-panel__kicker">Publishing reference</span>
            <h2 id="featured-guidance-title">Featured image playbook</h2>
          </div>
          <span>Carousel artwork · Up to five current slides</span>
        </div>

        <div className="featured-guidance__grid">
          <div className="featured-guidance__section">
            <span className="featured-guidance__icon featured-guidance__icon--gold">
              <FaLightbulb />
            </span>
            <div>
              <h3>Tips for stronger artwork</h3>
              <p>
                Use high-quality landscape images with clear text, strong
                contrast, and vibrant brand colors. Keep important content away
                from the edges so the carousel remains legible on smaller screens.
              </p>
              <div className="featured-guidance__facts">
                <span><strong>1500 × 400</strong> web</span>
                <span><strong>1200 × 675</strong> mobile</span>
                <span><strong>5 MB</strong> each</span>
                <span><strong>JPG · PNG · WEBP</strong></span>
              </div>
            </div>
          </div>

          <div className="featured-guidance__section">
            <span className="featured-guidance__icon featured-guidance__icon--green">
              <FaListOl />
            </span>
            <div>
              <h3>Publishing instructions</h3>
              <ol>
                <li>Select the brand from the brand menu.</li>
                <li>Choose the edition date; future dates are unavailable.</li>
                <li>Add an optional caption of up to 160 characters.</li>
                <li>Choose either the wide web artwork, the mobile artwork, or both.</li>
                <li>Position the selected 15:4 web crop or 16:9 mobile crop.</li>
                <li>Publish to append a new slide, including on a date already in use.</li>
                <li>Use the pencil on an archived slide to add or replace its other format.</li>
              </ol>
            </div>
          </div>

          <div className="featured-guidance__section">
            <span className="featured-guidance__icon featured-guidance__icon--blue">
              <FaSyncAlt />
            </span>
            <div>
              <h3>How it works</h3>
              <ul>
                <li>
                  Web and mobile artwork are stored as one edition using the
                  date-based names <code>frontpage_YYYY-MM-DD_web</code> and
                  <code>frontpage_YYYY-MM-DD_mobile</code>.
                </li>
                <li>
                  Publishing again for the same brand and date replaces the
                  active edition while preserving its historical record.
                </li>
                <li>
                  The archive displays the five most recent active editions.
                </li>
                <li>
                  Historical mobile-only editions remain available and use the
                  existing safe web fallback until a wide replacement is published.
                </li>
                <li>
                  Artwork appears in the landing-page carousel only when the
                  brand is enabled for landing promotion.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </main>
  </>
  );
};

export default FeaturedImageUpload;
